import { Component, OnInit } from '@angular/core';
import { FormGroup, ɵInternalFormsSharedModule, ReactiveFormsModule, FormControl, Validators } from '@angular/forms';
import { AppModule } from "../app.module";
import { SettingsService } from './settings.service';
import { NotificationService } from '../shared/notification.service';
import { MapSelect } from "../map/map-select/map-select";
import { IncidentLocation } from '../models/incident';
import { ClientSettings } from '../models/client';

@Component({
  selector: 'app-settings',
  imports: [ɵInternalFormsSharedModule, ReactiveFormsModule, AppModule, MapSelect],
  templateUrl: './settings.html',
  styleUrl: './settings.less'
})
export class Settings implements OnInit {

  isLoading = false;
  location?: IncidentLocation;

  constructor(private settingsService: SettingsService, private notification: NotificationService) { }

  settingsForm = new FormGroup({
    name: new FormControl('', Validators.required),
    tel: new FormControl('', [Validators.required, Validators.pattern('^\\+?[0-9]{10,13}$')]),
    address: new FormGroup({
      city: new FormControl(''),
      lat: new FormControl(0, [Validators.required, Validators.min(-90), Validators.max(90)]),
      lon: new FormControl(0, [Validators.required, Validators.min(-180), Validators.max(180)]),
    }),
    standardDelayTimeMins: new FormControl(0, [Validators.min(0)])
    
  });

  isFieldInvalid(fieldName: string): boolean {
  const field = this.settingsForm.get(fieldName);
  return !!(field && field.invalid && (field.dirty || field.touched));
}

getFieldError(fieldName: string): string {
  const field = this.settingsForm.get(fieldName);
  if (!field || !field.errors) return '';
  
  if (field.errors['required']) return 'This field is required';
  if (field.errors['minlength']) return `Minimum ${field.errors['minlength'].requiredLength} characters required`;
  if (field.errors['pattern']) return 'Invalid format';
  if (field.errors['min']) return `Minimum value is ${field.errors['min'].min}`;
  if (field.errors['max']) return `Maximum value is ${field.errors['max'].max}`;
  
  return 'Invalid value';
}

  ngOnInit(): void {
    this.isLoading = true;
    this.settingsService.getSettings().subscribe({
      next: (settings : ClientSettings) => {
        this.settingsForm.patchValue({
          name: settings.clientName,
          tel: settings.clientTel,
          address: {
            city: settings.addressCity,
            lat: settings.lat,
            lon: settings.lon
          },
          standardDelayTimeMins: settings.standardDelayTimeMins
        });
        this.location = {
          latitude: settings.lat,
          longitude: settings.lon,
          descriptionShort: '',
          description: null
        };

      },
      error: (err) => {
        this.notification.handleError(err, 'Failed to load settings.');
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  };


  public saveSettings() {
    if (!this.settingsForm.valid) {
    Object.values(this.settingsForm.controls).forEach(control => {
      control.markAsDirty();
      control.updateValueAndValidity();
      
      // For nested form groups (like address)
      if (control instanceof FormGroup) {
        Object.values(control.controls).forEach(nestedControl => {
          nestedControl.markAsDirty();
          nestedControl.updateValueAndValidity();
        });
      }
    });
    this.notification.showError('Please correct the errors before saving.');
    return;
  }

    this.isLoading = true;
    const formValue = this.settingsForm.getRawValue();

    this.settingsService.updateSettings({
      clientId: 0,
      clientName: formValue.name ?? '',
      clientTel: formValue.tel ?? '',
      addressCity: formValue.address?.city ?? '',
      lat: formValue.address?.lat ?? 0,
      lon: formValue.address?.lon ?? 0,
      standardDelayTimeMins: formValue.standardDelayTimeMins ?? 0
    }).subscribe({
      next: (updatedSettings) => {
        this.notification.showSuccess('Settings saved successfully.');
      },
      error: (err) => {
        this.notification.handleError(err, 'Failed to save settings.');
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }

  public findLocation() {
    window.navigator.geolocation.getCurrentPosition((position) => {
      this.settingsForm.patchValue({
        address: {
          lat: position.coords.latitude,
          lon: position.coords.longitude
        }
      });
      this.location = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        descriptionShort: '',
        description: null
      };
    });;
  }

  public onMapLocationSelected(location: IncidentLocation | null) {
    if (location) {
      this.settingsForm.patchValue({
        address: {
          lat: location.latitude,
          lon: location.longitude
        }
      });
    }
  }

  public getIncidentLocation(): IncidentLocation | undefined  {
    const lat = this.settingsForm.get('address.lat')?.value;
    const lon = this.settingsForm.get('address.lon')?.value;
    if (lat != null && lon != null) {
      return {
        latitude: lat,
        longitude: lon,
        descriptionShort: '',
        description: null
      };
    }
    return undefined;
  }
}
